// The counter book AT THE PAYMENTS CHECKPOINT — for the SUPERADMIN's screen.
//
// Two rails end at this checkpoint: the sales team's inquiries and the counter
// book (walk-in entries + the admin's own direct sales). The superadmin's
// Payments page listed only the first, so a counter sale the admin never got
// round to approving was real money sitting in limbo that no superadmin screen
// showed. This section closes that gap: same queue, same three actions
// (approve · correct the price · collect the balance) the ops workspace has,
// so the superadmin can act when the admin misses one — not just watch.
//
// It renders the SAME cards as the ops Payments board; the two screens can
// never drift apart on how a counter sale looks or what may be done to it.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiCreditCard, FiRefreshCw, FiShoppingBag } from 'react-icons/fi';
import { Alert, Button } from '../common/index.js';
import { CounterSaleCard, CounterPriceModal } from './PaymentsBoard.jsx';
import { BalanceModal } from './DispatchBoard.jsx';

const formatPrice = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '';
};

const TABS = [
  { value: 'to_approve', label: 'To approve' },
  { value: 'balance_due', label: 'Balance due' },
  { value: 'approved', label: 'Approved' },
];

// How much of the approved book to show. The queues themselves (to approve,
// balance due) are NEVER capped — money waiting must not fall off a page.
const BOOK_LIMIT = 50;

const CounterCheckpoint = ({ api, onChanged }) => {
  const [tab, setTab] = useState('to_approve');
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState([]);
  const [book, setBook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [priceTarget, setPriceTarget] = useState(null);
  const [balanceTarget, setBalanceTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Unapproved entries and open balances are fetched for ANY day — a
      // night-book entry checked the next morning must never age out of view.
      const [toApprove, openBalances, approved] = await Promise.all([
        api.list({ confirmed: 0 }),
        api.list({ open_balance: 1 }),
        tab === 'approved' ? api.list({ confirmed: 1, limit: BOOK_LIMIT }) : null,
      ]);
      setPending((toApprove.data ?? toApprove).sales || []);
      setOpen((openBalances.data ?? openBalances).sales || []);
      if (approved) setBook((approved.data ?? approved).sales || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load the counter book');
    } finally {
      setLoading(false);
    }
  }, [api, tab]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const approve = async (sale) => {
    setBusyId(sale.id);
    setError('');
    try {
      await api.confirmPayment(sale.id, true);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not approve the payment');
    } finally {
      setBusyId(null);
    }
  };

  const savePrice = async (body) => {
    setSubmitting(true);
    setModalError('');
    try {
      await api.update(priceTarget.id, body);
      setPriceTarget(null);
      refresh();
    } catch (e) {
      const d = e?.response?.data;
      setModalError(d?.error || d?.total_amount || d?.advance_amount || e.message
        || 'Could not save the price');
    } finally {
      setSubmitting(false);
    }
  };

  const collectBalance = async (mode) => {
    setSubmitting(true);
    setModalError('');
    try {
      await api.saleBalance(balanceTarget.id, { paid: true, mode });
      setBalanceTarget(null);
      refresh();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not record the balance');
    } finally {
      setSubmitting(false);
    }
  };

  const rows = tab === 'to_approve' ? pending : tab === 'balance_due' ? open : book;
  const dueTotal = useMemo(
    () => open.reduce((sum, s) => sum + (Number(s.balance_due) || 0), 0),
    [open],
  );
  const counts = { to_approve: pending.length, balance_due: open.length, approved: null };

  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
            <FiShoppingBag className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-base font-bold text-slate-900">Counter sales at this checkpoint</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Walk-in entries and the admin's direct sales wait here for the same approval as
              the deals above. If the admin misses one, you can approve it, correct the price,
              or collect the balance from here.
            </p>
          </div>
        </div>
        <Button variant="secondary" onClick={load} disabled={loading} className="gap-2">
          <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {error && <div className="mt-3"><Alert type="error" message={error} /></div>}

      {(pending.length > 0 || dueTotal > 0) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {pending.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-sky-200 bg-sky-50 px-3 py-1.5 text-sm font-bold text-sky-700">
              <FiCheckCircle className="h-4 w-4" />
              {pending.length} counter sale{pending.length === 1 ? '' : 's'} waiting for approval
            </span>
          )}
          {dueTotal > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-bold text-rose-700">
              <FiCreditCard className="h-4 w-4" />
              {formatPrice(dueTotal)} still to collect on {open.length} advance
              {open.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const active = tab === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
            >
              {t.label}
              {counts[t.value] > 0 && (
                <span className={`rounded-full px-1.5 text-xs font-bold ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                  {counts[t.value]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-3">
        {loading ? (
          <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white py-10 text-center">
            <FiShoppingBag className="mx-auto mb-2 h-7 w-7 text-slate-200" />
            <p className="text-sm text-slate-400">
              {tab === 'to_approve' ? 'Every counter sale has been approved'
                : tab === 'balance_due' ? 'No open balances on counter sales'
                  : 'Nothing approved yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((sale) => (
              <CounterSaleCard
                key={sale.id}
                sale={sale}
                busy={busyId === sale.id}
                onApprove={approve}
                onEditPrice={(s) => { setModalError(''); setPriceTarget(s); }}
                onCollectBalance={(s) => { setModalError(''); setBalanceTarget(s); }}
              />
            ))}
          </div>
        )}
      </div>

      {priceTarget && (
        <CounterPriceModal
          sale={priceTarget}
          submitting={submitting}
          error={modalError}
          onClose={() => { if (!submitting) { setPriceTarget(null); setModalError(''); } }}
          onSave={savePrice}
        />
      )}
      {balanceTarget && (
        <BalanceModal
          order={{
            customer_name: balanceTarget.customer?.name,
            car: [balanceTarget.car_brand, balanceTarget.car_model, balanceTarget.car_year]
              .filter(Boolean).join(' '),
            payment: { balance_due: balanceTarget.balance_due },
          }}
          submitting={submitting}
          error={modalError}
          onClose={() => { if (!submitting) { setBalanceTarget(null); setModalError(''); } }}
          onConfirm={collectBalance}
        />
      )}
    </section>
  );
};

export default CounterCheckpoint;
